/**
 * The Studio's own typed event map — STUDIO_DESIGN.md §8.
 *
 * Owner ruling: each editor owns its map; the emitter machinery is the shared part
 * (utils/metric-events-typed.ts). Nothing here is exported to another editor, and nothing
 * here imports one.
 *
 * NO ENVELOPE FIELDS. `MetricsService.emit` already stamps `platform`, `pageUrl`, `wsId` and
 * `sessionId` onto every event (services/metrics.service.ts:144-155). §8 writes
 * `STUDIO_OPENED{source, platform}`, but declaring `platform` here would ship a SECOND value
 * that disagrees with the envelope's the moment the window is resized between mount and
 * emit. A spec reads this file and fails if any of the four appears.
 *
 * P5a emits two of these — STUDIO_CAPS from the capability probe and STUDIO_PROXY_WAIT from
 * the rendition poller. The other fourteen are declared here and emitted by the views (P5b)
 * and the integration (P5c); the names have to exist before their first emitter, and P5a has
 * the first emitter.
 */
import { MetricEventName } from '../../../types/metric-events';
import { emitTyped } from '../../../utils/metric-events-typed';
// Type-only, and from the two leaves on purpose: `model/wire.ts` is GENERATED from the
// composition schema both server tiers validate against, and `model/violations.ts` mirrors
// the Kotlin code set. Neither imports anything (verified), and `state/` already depends on
// `model/` (persistence.ts:46, studio-url.ts:17), so this closes no loop — and `import type`
// is erased outright, so it is not even a runtime edge.
import type { CompositionViolationCodeValue, STUDIO_UNAVAILABLE } from '../model/violations';
import type { Fit, PhotoClip, Transition } from '../model/wire';
// The sixth closed-set attribute, and the one leaf outside `model/`. `media/types.ts` imports
// NOTHING, so `state -> media/types` is an edge and not a cycle even though `media/renditions.ts`
// imports the emitter below — and `import type` is erased, so at run time there is no edge at all.
// `MediaKind` joins it for `sourceFormat` below, off the same leaf and the same erased edge.
import type { MediaKind, RenditionKind } from '../media/types';

/**
 * Where the user came into the editor from. A closed set so it can be grouped on.
 *
 * `'restore-url'` was declared here and dropped in the P5c final fix: §8 says `STUDIO_OPENED` is
 * "not on restore", so it could never have had an emitter — a `?sid=` arrival raises
 * `STUDIO_OPENED{source:'reedit'}` alongside `STUDIO_RESTORED`, and a `LATEST` pick-up raises the
 * restore alone. `'downloads-chip'` stays without one on purpose: it is ruling 5, and P6.3 ships
 * the /downloads chip that emits it.
 */
export type StudioOpenSource = 'tab' | 'nudge' | 'reedit' | 'downloads-chip';

/**
 * The five §8 attributes whose values are a closed set the model already defines.
 *
 * They were `string` when this file landed, which is the one thing a typed emitter must not
 * do with a value that has a vocabulary: §8 charts the transition / motion / fit / aspect
 * DISTRIBUTIONS off `STUDIO_EXPORT_START`, so a view that emits `'kenburns'` where the wire
 * says `'SLOW_ZOOM'` splits a dashboard panel and nothing anywhere goes red. Importing the
 * unions makes that a `tsc` error at the call site instead (review I1).
 *
 * `Fit` and the two below are the schema's own; a value the renderer cannot be asked for is
 * a value the editor cannot produce, so there is deliberately no escape hatch. If P5b ever
 * needs a summary value the schema has no word for — `'MIXED'` across clips, say — it widens
 * the union HERE, as one considered edit, rather than reaching for a free string.
 */
export type StudioMotion = NonNullable<PhotoClip['motion']>;

/** The six transitions the schema allows (`model/wire.ts:55`). */
export type StudioTransition = Transition['type'];

/**
 * `STUDIO_EXPORT_FAILED.code`: the seventeen closed codes, the kill-switch token, and one
 * bucket for anything else.
 *
 * `types/metric-events.ts` promises this event carries "the closed `CompositionViolationCode`"
 * and now the type keeps that promise. `studio_unavailable` is outside the closed set on
 * purpose (`model/violations.ts:97-100`) and is a real refusal a user can hit. `'unknown'` is
 * the bucket for a server code `isKnownViolationCode()` rejects — normalise there, because an
 * unrecognised string passed straight through is how an attribute's cardinality becomes
 * unbounded in `frontend-events`.
 */
export type StudioFailureCode = CompositionViolationCodeValue | typeof STUDIO_UNAVAILABLE | 'unknown';

/**
 * `STUDIO_EXPORT_START.aspect`: the output shape, as a `FRAME_PRESETS` id.
 *
 * The one closed set of the five with no union to import — `FramePreset.id` is typed `string`
 * (`model/frame.ts:52`), and widening that is P5a.4's file, not this task's. So the list lives
 * here and `studio-events.spec.ts` asserts at run time that every id in `FRAME_PRESETS` is a
 * member of it, which catches a preset added there and not here. `custom` is the frame sheet's
 * hand-typed size, which no preset covers.
 */
const STUDIO_ASPECT_LIST = [
    'original', 'landscape', 'portrait', 'square', 'social', 'classic', 'custom',
] as const;

export type StudioAspect = (typeof STUDIO_ASPECT_LIST)[number];

export const STUDIO_ASPECTS: readonly StudioAspect[] = Object.freeze(STUDIO_ASPECT_LIST);

/**
 * §8: "once per feature per session". That section's fourteen, plus `rotate` and `flip`.
 *
 * The two additions are an owner ruling (2026-09-12): EXIF orientation is normalised once on
 * import, and a rotate or a flip after that is an edit like any other — so they belong in the
 * same mix as trim and reframe, and leaving them out would have made the feature histogram
 * quietly wrong rather than merely short. It costs professorx nothing: `STUDIO_FEATURE_USED`
 * is allow-listed by NAME and `feature` never becomes a Prometheus label, so the attribute's
 * cardinality is a Mongo question. §8's prose still lists fourteen and needs the two names
 * (P6.2 doc sync).
 *
 * The list is the source and the type is derived from it, so a feature can never be in one
 * and not the other. P5b tracks "once per session" with a `Set<StudioFeature>`.
 */
const STUDIO_FEATURE_LIST = [
    'trim', 'reorder', 'hold', 'motion', 'transition', 'fit', 'reframe', 'music', 'mute',
    'volume', 'duplicate', 'split', 'frame', 'undo', 'rotate', 'flip',
] as const;

export type StudioFeature = (typeof STUDIO_FEATURE_LIST)[number];

export const STUDIO_FEATURES: readonly StudioFeature[] = Object.freeze(STUDIO_FEATURE_LIST);

export type ProxyWaitBucket = '0-1s' | '1-3s' | '3-10s' | '10-30s' | '30s+';

/** Bucketed, never raw: a millisecond value would be an unbounded tag if it ever escaped. */
export function proxyWaitBucket(waitedMs: number): ProxyWaitBucket {
    if (waitedMs < 1000) return '0-1s';
    if (waitedMs < 3000) return '1-3s';
    if (waitedMs < 10_000) return '3-10s';
    if (waitedMs < 30_000) return '10-30s';
    return '30s+';
}

/**
 * **What KIND of file a clip is** — the seventh closed-set attribute, and the one the Studio's
 * first export incident had no way to ask for (EXPORT-FAILURE-9vbjspipq2gw, 2026-09-25).
 *
 * That failure was specific to ANIMATED PNG: the wire omitted the clip's end, `ffprobe` answers
 * `duration=N/A` for an APNG, the end resolved to 0 and the render was refused. Every event in
 * this map could see that an export failed and with which code; none of them could see that the
 * clip was an APNG, so "every animated source fails" was not a question the frontend data could
 * answer — it had to be answered by hand, from one workflow id.
 *
 * THE VOCABULARY IS CLOSED, and that is the whole design constraint: an extension comes off a
 * USER'S FILE NAME, so `ext` reaching `frontend-events` unnormalised is how this attribute's
 * cardinality becomes unbounded. The seven values are the classes that behave DIFFERENTLY at the
 * far end, which is the only distinction worth a bucket:
 *
 *   `photo`           a still image — carries `holdMs`, never needs a probe, cannot fail this way
 *   `gif`             animated GIF — `ffprobe` times it correctly, and it never had the bug
 *   `apng`            animated PNG — `duration=N/A` AND `nb_frames=N/A`; the incident's own class
 *   `webp_animated`   animated WebP — the other `N/A` class, the same latent failure
 *   `animated_other`  animated AVIF/HEIC and anything else the sniffer calls animated; they
 *                     answer at the format block, so they are a THIRD behaviour and not junk
 *   `video`           a real video container the edge can always probe
 *   `other`           audio, and any source no runtime could describe (a restored project before
 *                     its files rehydrate). The bucket, never a passthrough.
 *
 * `png` and `apng` both map to `apng` when the bytes are animated, because an animated PNG is a
 * valid `.png` — which is exactly how the incident's `elephant.png` arrived.
 */
const STUDIO_SOURCE_FORMAT_LIST = [
    'photo', 'gif', 'apng', 'webp_animated', 'animated_other', 'video', 'other',
] as const;

export type StudioSourceFormat = (typeof STUDIO_SOURCE_FORMAT_LIST)[number];

export const STUDIO_SOURCE_FORMATS: readonly StudioSourceFormat[] = Object.freeze(STUDIO_SOURCE_FORMAT_LIST);

/**
 * Normalise one source to that closed set. The ONLY string → format conversion there is.
 *
 * `animated` is the browser's own answer from the bytes (`media/animated.ts`'s `sniffAnimated`,
 * held on `ClipRuntime.animated`), never a guess from the extension: a still `.png` is a `photo`
 * and an animated one is an `apng`, and the extension alone cannot tell them apart. An input this
 * cannot describe — no runtime, an unknown `kind` — is `other` rather than a passthrough.
 */
export function sourceFormat(source?: {
    ext?: string | null; kind?: MediaKind | null; animated?: boolean | null;
} | null): StudioSourceFormat {
    if (!source) return 'other';
    if (source.kind === 'video') return 'video';
    if (source.kind !== 'image') return 'other';
    if (!source.animated) return 'photo';
    // Lower-cased defensively. `ClipSource.ext` is already lower-case and dotless by contract, and
    // the point of this function is that a value off a user's file name never escapes it anyway.
    const ext = typeof source.ext === 'string' ? source.ext.toLowerCase() : '';
    if (ext === 'gif') return 'gif';
    if (ext === 'png' || ext === 'apng') return 'apng';
    if (ext === 'webp') return 'webp_animated';
    return 'animated_other';
}

/**
 * The formats an export CONTAINS, as one attribute: the distinct values present, in
 * `STUDIO_SOURCE_FORMATS` order, joined with `+` — `'apng+video'`, `'photo'`, `'none'`.
 *
 * BOUNDED BY CONSTRUCTION at 2^7 = 128 values (`'none'` is the empty one), because it is built by
 * FILTERING the closed list rather than by joining what the caller handed over: a value outside
 * the set cannot survive the filter, and the canonical order means `[apng, photo]` and
 * `[photo, apng]` can never open two panels for one mix. The spec enumerates all 128 and asserts
 * membership.
 *
 * The type is BRANDED, so the `formats` attribute cannot be given a bare string. That is not
 * ceremony: the values this must never carry — `'image/apng'`, `'elephant.png'`, `'.PNG'` — are
 * all strings, and a plain `string` attribute would take every one of them at a call site that
 * looks correct. Only this function can make the type, so normalisation is not a convention.
 */
export type StudioFormatMix = string & { readonly __studioFormatMix: 'closed' };

/** What `formatMix` answers for an export with no clips at all. */
export const STUDIO_FORMAT_MIX_NONE = 'none' as StudioFormatMix;

export function formatMix(formats: Iterable<StudioSourceFormat>): StudioFormatMix {
    const present = new Set<StudioSourceFormat>(formats);
    const tokens = STUDIO_SOURCE_FORMATS.filter((format) => present.has(format));
    return (tokens.length ? tokens.join('+') : STUDIO_FORMAT_MIX_NONE) as StudioFormatMix;
}

/**
 * **What the user was trying to export** — carried by all three export outcomes.
 *
 * EXPORT-FAILURE-9vbjspipq2gw: `STUDIO_EXPORT_FAILED` said `code` and `clipIndexed` and nothing
 * about the project the code was about, so a spike in one code could not be read — was it every
 * export, or every export with an animated clip in it? The same four facts ride on START (the
 * denominator: how many exports of this shape were attempted), on FAILED (the server's verdict)
 * and on BLOCKED (the client's own refusal), so the ratio is a division of two series that are
 * sliced the same way.
 *
 * Attributes on existing names, never new names: professorx allow-lists by NAME
 * (`FrontendEventNames.kt`) and the drift guard in `studio-events.spec.ts` holds the two lists
 * equal, so a new name is a two-repo change and a new attribute is not.
 *
 * This interface is the SESSION API's parameter type. The map below spells the same four fields
 * out INLINE in each of the three entries rather than intersecting this in, because the map's
 * envelope guards read the map's own source text and review M4 already established that an entry
 * written as a named type moves its fields out of that slice — an intersection is that same hole.
 * `studio-events.spec.ts` asserts the three entries and this interface carry the same four names,
 * so the duplication cannot drift.
 */
export interface StudioExportShape {
    /** Visual clips on the timeline. The music track is `hasMusic` on START, not a clip here. */
    clips: number;
    formats: StudioFormatMix;
    /** Clips whose source the browser sniffed as animated. */
    animatedClips: number;
    /**
     * Clips placed as VIDEO whose length NOBODY has measured — an animated image the browser
     * could not time, which the edge cannot time either.
     *
     * The same rule `buildExportRequest` refuses as `unmeasured` and the export gate blocks on,
     * so a non-zero value on a `STUDIO_EXPORT_START` means one of those two leaked. On a BLOCKED
     * it is the population the refusal is about.
     */
    unmeasuredClips: number;
}

/**
 * One entry per Studio event, with the attributes §8 specifies.
 *
 * A `type` alias, not an `interface`, and that is load-bearing rather than stylistic: an
 * `interface … extends Record<string, MetricAttrs>` makes `keyof` resolve to `string | number`
 * and collapses the typed emitter's event parameter to `never`, so every call site becomes a
 * `tsc` error while jest stays green (babel erases types). Keyed on the sixteen enum members,
 * `keyof` is exactly those sixteen — which is also what `StudioEventName` below is.
 *
 * `NUDGE_*` carry `files` — the count that triggered the nudge — because acceptance almost
 * certainly varies with it and a ratio of two series is only as useful as its slices. §8
 * names no attributes for them; this is the smallest addition that makes the guardrail
 * ("nudge acceptance 15–35 %") answerable, and P5c may widen it.
 */
export type StudioEventAttrs = {
    [MetricEventName.STUDIO_OPENED]: { source: StudioOpenSource };
    [MetricEventName.STUDIO_FIRST_EDIT]: { kind: StudioFeature; msSinceOpen: number };
    /**
     * `formats`, `animatedClips` and `unmeasuredClips` are `StudioExportShape`; `wireTotalS` is
     * THE CLIENT'S OWN PLAN OF WHAT IT SENT (EXPORT-FAILURE-9vbjspipq2gw).
     *
     * `totalS` is `planOf(doc)` — the timeline the editor DREW, with everything the browser
     * measured. `wireTotalS` is `plan(theWire)` with no probe information at all: the length the
     * RECIPIENT computes from the payload alone. The two are equal on every healthy export, and
     * they can only diverge when the wire leaves out something its reader cannot recover — which
     * is precisely the incident (editor 7.4 s, edge 6.0 s, because an APNG's omitted end resolved
     * to 0). Reporting one and not the other made that divergence findable only by rebuilding the
     * payload by hand from a workflow id. Two numbers on one event make it a query.
     */
    [MetricEventName.STUDIO_EXPORT_START]: {
        clips: number; formats: StudioFormatMix; animatedClips: number; unmeasuredClips: number;
        totalS: number; wireTotalS: number;
        hasMusic: boolean; hasVideo: boolean; hasAnimated: boolean;
        aspect: StudioAspect; motion: StudioMotion; transition: StudioTransition; fit: Fit;
        mutedClips: number; reframed: number; undos: number; msSinceOpen: number;
    };
    [MetricEventName.STUDIO_EXPORT_COMPLETE]: { ms: number };
    [MetricEventName.STUDIO_EXPORT_FAILED]: {
        code: StudioFailureCode; clipIndexed: boolean;
        clips: number; formats: StudioFormatMix; animatedClips: number; unmeasuredClips: number;
    };
    /**
     * `reason` is a closed vocabulary P5c owns — never a message, a filename or a stack.
     *
     * It carries the same `StudioExportShape` as FAILED because a CLIENT-side refusal is a dead
     * end in exactly the way a server one is (EXPORT-FAILURE-9vbjspipq2gw's fix added one:
     * `reason:'unmeasured'`, an animated image nobody could time). A refusal nobody can slice by
     * what was being refused is only invisible in a different place.
     */
    [MetricEventName.STUDIO_EXPORT_BLOCKED]: {
        reason: string;
        clips: number; formats: StudioFormatMix; animatedClips: number; unmeasuredClips: number;
    };
    [MetricEventName.STUDIO_FEATURE_USED]: { feature: StudioFeature };
    /**
     * IT1-W1 (saas #9): the four flows a post-launch decision needs a signal for and that had none --
     * Start over, a live expiry sweep under an open tab, "Continue in the background" off the render
     * panel, "Back to Studio" off it -- ride on the session's end as counts and flags. Attributes on
     * an existing name rather than new names: professorx allow-lists by NAME (`FrontendEventNames.kt`)
     * and the drift guard in `studio-events.spec.ts` holds the two lists equal.
     */
    [MetricEventName.STUDIO_SESSION_END]: {
        edits: number; undos: number; durationS: number; exported: boolean;
        startOvers: number; expiries: number; backgrounded: boolean; backToEditor: boolean;
        /**
         * DIAG-render-panel-stall.md follow-up: how often the render watchdog gave up on a
         * stalled render (`state/session.ts`'s `render_lost_contact` flow) and, of those, how
         * many came back (`render_lost_contact_recovered`) before the session ended. The
         * difference is never negative and is the population that matters — a session that ends
         * with `lostContacts > lostContactsRecovered` is one the user was left stranded in,
         * which is the number the favoured stalled-commit explanation needs to be sized.
         */
        lostContacts: number; lostContactsRecovered: number;
    };
    /** `step` is a closed vocabulary P5b owns — the editor step's name, never free text. */
    [MetricEventName.STUDIO_ABANDON]: { clips: number; step: string };
    [MetricEventName.STUDIO_RESTORED]: { from: 'url' | 'latest' | 'index'; outcome: 'ok' | 'expired' | 'partial' | 'failed' };
    /**
     * `kind` is the rendition's wire name, closed by `media/types.ts` and mirrored from
     * `RenditionKind.kt` — the sixth closed-set attribute and the last one that was still a
     * free `string`. P5a.6's review I1 named the first five (aspect, motion, transition, fit,
     * code); this one is P5a.7a's review M2, which is why the directive count moved 5 → 6. §8 slices the proxy-wait histogram BY kind, so a caller
     * emitting `'p540'` or `'strip_dense'` would open a panel nobody notices is split.
     */
    [MetricEventName.STUDIO_PROXY_WAIT]: { kind: RenditionKind; bucket: ProxyWaitBucket };
    [MetricEventName.STUDIO_CAPS]: {
        webgl2: boolean; rvfc: boolean; webcodecs: boolean; offscreen: boolean;
        imagebitmap: boolean; offlineaudio: boolean; h264: boolean;
    };
    [MetricEventName.NUDGE_SHOWN]: { files: number };
    [MetricEventName.NUDGE_ACCEPTED]: { files: number };
    [MetricEventName.NUDGE_DECLINED]: { files: number };
    [MetricEventName.NUDGE_DISMISSED]: { files: number };
};

/** The sixteen, as a type. Derived from the map so the two can never disagree. */
export type StudioEventName = keyof StudioEventAttrs;

/**
 * The sixteen, as data, so a test can compare them with professorx's allow-list.
 *
 * Typed `readonly StudioEventName[]`: a name here that the map does not declare is a `tsc`
 * error, and a name the map declares but this list omits is caught by the spec, which counts
 * sixteen and compares the set with `FrontendEventNames.ALLOWED`.
 */
export const STUDIO_EVENT_NAMES: readonly StudioEventName[] = Object.freeze([
    MetricEventName.STUDIO_OPENED,
    MetricEventName.STUDIO_FIRST_EDIT,
    MetricEventName.STUDIO_EXPORT_START,
    MetricEventName.STUDIO_EXPORT_COMPLETE,
    MetricEventName.STUDIO_EXPORT_FAILED,
    MetricEventName.STUDIO_EXPORT_BLOCKED,
    MetricEventName.STUDIO_FEATURE_USED,
    MetricEventName.STUDIO_SESSION_END,
    MetricEventName.STUDIO_ABANDON,
    MetricEventName.STUDIO_RESTORED,
    MetricEventName.STUDIO_PROXY_WAIT,
    MetricEventName.STUDIO_CAPS,
    MetricEventName.NUDGE_SHOWN,
    MetricEventName.NUDGE_ACCEPTED,
    MetricEventName.NUDGE_DECLINED,
    MetricEventName.NUDGE_DISMISSED,
]);

/**
 * The Studio's bound emitter. Never throws, never fires during SSG.
 *
 * `E extends StudioEventName` and nothing more: every member of that union is already a
 * `MetricEventName`, so the `Extract<…>` this used to carry was a no-op that read as doubt
 * the code does not have (review M6).
 */
export function emitStudio<E extends StudioEventName>(
    event: E,
    attributes: StudioEventAttrs[E],
): void {
    emitTyped<StudioEventAttrs, E>(event, attributes);
}
